import { purchaseOrderReference } from '@sitzmark/core';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { DocumentGrid } from './document-grid-view.js';
import './receiving-document.css';

/**
 * The receiving document (SCOPE §6.1).
 *
 * *"Receiving document: printable/PDF per RO — units received, unit cost,
 * extension, totals — for their paper trail."* A named client requirement that
 * sat at zero while the plumbing it needs — HTML plus a print stylesheet, the
 * §7.1 architecture — had been in place since receipts.
 *
 * **Letter paper, not the receipt roll.** A receipt is 80mm because it goes in
 * a customer's hand; this goes in a folder next to the vendor's invoice, and
 * gets read alongside it. Same architecture, different `@page`.
 *
 * It reports **what arrived on this receipt**, at the cost this receipt was
 * charged — not the purchase order's quantities, and not the variant's
 * weighted moving average, which has since absorbed this delivery and every
 * one before it (§18.1-2). The whole reason the paperwork exists is that those
 * three numbers differ.
 */

const PAGE_RULE = '@page { size: letter; margin: 0.6in; }';

type Document = Awaited<ReturnType<typeof api.purchasing.receivingDocument.query>>;
type Settings = Awaited<ReturnType<typeof api.settings.get.query>>;

export function useReceivingDocument() {
  const [printing, setPrinting] = useState<{
    document: Document;
    settings: Settings | null;
  } | null>(null);

  const print = useCallback((document: Document, settings: Settings | null) => {
    const style = window.document.createElement('style');
    style.textContent = PAGE_RULE;
    window.document.head.appendChild(style);

    try {
      // Rendered synchronously before `window.print()` — the same reason the
      // label run does it. React would otherwise batch the state update until
      // after the print dialog had already captured a blank page.
      flushSync(() => setPrinting({ document, settings }));
      window.print();
    } finally {
      setPrinting(null);
      style.remove();
    }
  }, []);

  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <ReceivingDocument document={printing.document} settings={printing.settings} />
          </div>,
          window.document.body,
        );

  return { print, frame };
}

/**
 * Loads the document and prints it, which is the whole interaction.
 *
 * One press from the bench (§6.1). The query is fetched on demand rather than
 * with the bench, because the paperwork is wanted once and the receiving screen
 * is on the critical path of a delivery (§2-5).
 */
export function useReceivingDocumentPrinter(settings: Settings | null) {
  const printer = useReceivingDocument();
  /*
   * An action, not a cached query (2026-10-02): the print used to run inside a
   * query's loader, and the app keeps a query fresh for fifteen seconds — so a
   * second press on the same order, a cancelled dialog and a quick retry,
   * found the cached copy, ran no loader, and printed nothing. Every press now
   * loads and prints.
   */
  const load = useMutation({
    mutationFn: (receivingOrderId: string) =>
      api.purchasing.receivingDocument.query({ receivingOrderId }),
    onSuccess: (loaded) => printer.print(loaded, settings),
  });

  return {
    frame: printer.frame,
    busy: load.isPending,
    error: load.error,
    print: (receivingOrderId: string) => load.mutate(receivingOrderId),
  };
}

export function ReceivingDocument({
  document,
  settings,
}: {
  document: Document;
  settings: Settings | null;
}) {
  const header = document.receivingOrder;
  const store = settings?.store ?? null;

  return (
    <article className="rodoc">
      <header className="rodoc__head">
        <div>
          <div className="rodoc__store">{store?.legalName ?? store?.name ?? 'Shekko'}</div>
          {store?.addressLine1 == null ? null : <div>{store.addressLine1}</div>}
          {store?.city == null ? null : (
            <div>
              {store.city}
              {store.region == null ? '' : `, ${store.region}`} {store.postalCode ?? ''}
            </div>
          )}
          {store?.phone == null ? null : <div>{store.phone}</div>}
        </div>
        <div className="rodoc__title">
          <h1>Receiving document</h1>
          <div className="rodoc__number">{header.number}</div>
        </div>
      </header>

      <dl className="rodoc__meta">
        <div>
          <dt>Vendor</dt>
          <dd>{header.vendorName}</dd>
        </div>
        <div>
          <dt>Purchase order</dt>
          {/* Named plainly when there is none: receiving without a PO is a real
              workflow (§6.1), and a blank cell reads as missing data. */}
          <dd>{header.purchaseOrderNumber ?? 'No purchase order'}</dd>
        </div>
        {header.purchaseOrderReference == null ? null : (
          <div>
            <dt>Their order number</dt>
            <dd>{header.purchaseOrderReference}</dd>
          </div>
        )}
        <div>
          <dt>Received</dt>
          <dd>{header.receivedAt === null ? '—' : new Date(header.receivedAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Received by</dt>
          <dd>{header.receivedByName ?? '—'}</dd>
        </div>
      </dl>

      {/* The grid (item 20): the same style-grouped shape the purchase order
          prints, so the two papers read against each other line for line. */}
      <DocumentGrid lines={document.lines} />

      <p className="rodoc__notes">
        {document.totals.lines} line{document.totals.lines === 1 ? '' : 's'} ·{' '}
        {document.totals.units} units · {money(document.totals.costCents)} at cost
      </p>

      {header.notes == null ? null : <p className="rodoc__notes">{header.notes}</p>}

      {/*
        Space to sign. This document's job is to be the shop's evidence that a
        delivery was checked, and the signature is what makes it that rather
        than a printout.
      */}
      <div className="rodoc__sign">
        <div>
          <span className="rodoc__rule" />
          Checked by
        </div>
        <div>
          <span className="rodoc__rule" />
          Date
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------

type CumulativeDocument = Awaited<
  ReturnType<typeof api.purchasing.cumulativeReceivingDocument.query>
>;

/**
 * One paper per purchase order, everything received to date (item 34).
 *
 * Three deliveries used to be three papers — true to the data, and not what
 * gets filed against the vendor's invoice. This is the default print now; the
 * per-receipt document above stays for checking a single delivery against a
 * single packing slip.
 */
export function CumulativeReceivingDocument({
  document,
  settings,
}: {
  document: CumulativeDocument;
  settings: Settings | null;
}) {
  const header = document.purchaseOrder;
  const reference = purchaseOrderReference(header.vendorReference);
  const store = settings?.store ?? null;
  const complete = document.outstandingUnits === 0;

  return (
    <article className="rodoc">
      <header className="rodoc__head">
        <div>
          <div className="rodoc__store">{store?.legalName ?? store?.name ?? 'Shekko'}</div>
          {store?.addressLine1 == null ? null : <div>{store.addressLine1}</div>}
          {store?.city == null ? null : (
            <div>
              {store.city}
              {store.region == null ? '' : `, ${store.region}`} {store.postalCode ?? ''}
            </div>
          )}
          {store?.phone == null ? null : <div>{store.phone}</div>}
        </div>
        <div className="rodoc__title">
          <h1>Receiving document</h1>
          <div className="rodoc__number">{header.number}</div>
        </div>
      </header>

      <dl className="rodoc__meta">
        <div>
          <dt>Vendor</dt>
          {/* The block item 25 asked for: who to call, where to send it back. */}
          <dd>
            {header.vendorName}
            {header.vendorContactName == null ? '' : ` · attn ${header.vendorContactName}`}
            {header.vendorAddressLine1 == null ? null : (
              <>
                <br />
                {header.vendorAddressLine1}
              </>
            )}
            {header.vendorCity == null && header.vendorRegion == null ? null : (
              <>
                <br />
                {[header.vendorCity, header.vendorRegion].filter(Boolean).join(', ')}{' '}
                {header.vendorPostalCode ?? ''}
              </>
            )}
            {header.vendorPhone == null ? null : (
              <>
                <br />
                {header.vendorPhone}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Purchase order</dt>
          <dd>{header.number}</dd>
        </div>
        {/* The vendor's number, or a Rain order's old one named as ours (FB-1130). */}
        {reference === null ? null : (
          <div>
            <dt>{reference.label}</dt>
            <dd>{reference.value}</dd>
          </div>
        )}
        <div>
          <dt>Received to date</dt>
          <dd>
            {document.totals.units} units
            {complete ? ' · complete' : ` · ${document.outstandingUnits} still outstanding`}
          </dd>
        </div>
      </dl>

      <DocumentGrid lines={document.lines} />

      <p className="rodoc__notes">
        {document.totals.lines} line{document.totals.lines === 1 ? '' : 's'} ·{' '}
        {document.totals.units} units · {money(document.totals.costCents)} at cost
      </p>

      {/* The history block: each delivery, one clause, oldest first. */}
      <p className="rodoc__history">
        {document.history
          .map(
            (receipt, index) =>
              `${receipt.units} unit${receipt.units === 1 ? '' : 's'}${index > 0 ? ' more' : ''} on ${
                receipt.receivedAt === null
                  ? '—'
                  : new Date(receipt.receivedAt).toLocaleDateString()
              } (${receipt.number})`,
          )
          .join(' · ')}
        {complete && document.history.length > 0 ? ' · complete' : ''}
      </p>

      <div className="rodoc__sign">
        <div>
          <span className="rodoc__rule" />
          Checked by
        </div>
        <div>
          <span className="rodoc__rule" />
          Date
        </div>
      </div>
    </article>
  );
}

/** Load the cumulative document for one PO and print it — one press. */
export function useCumulativeReceivingPrinter(settings: Settings | null) {
  const [printing, setPrinting] = useState<CumulativeDocument | null>(null);

  const print = useCallback((document: CumulativeDocument) => {
    const style = window.document.createElement('style');
    style.textContent = PAGE_RULE;
    window.document.head.appendChild(style);
    try {
      flushSync(() => setPrinting(document));
      window.print();
    } finally {
      setPrinting(null);
      style.remove();
    }
  }, []);

  /*
   * An action, not a cached query (2026-10-02): the print used to run inside a
   * query's loader, and the app keeps a query fresh for fifteen seconds — so a
   * second press on the same order, a cancelled dialog and a quick retry,
   * found the cached copy, ran no loader, and printed nothing. Every press now
   * loads and prints.
   */
  const query = useMutation({
    mutationFn: (purchaseOrderId: string) =>
      api.purchasing.cumulativeReceivingDocument.query({ purchaseOrderId }),
    onSuccess: (loaded) => print(loaded),
  });

  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <CumulativeReceivingDocument document={printing} settings={settings} />
          </div>,
          window.document.body,
        );

  return {
    frame,
    busy: query.isPending,
    error: query.error,
    print: (purchaseOrderId: string) => query.mutate(purchaseOrderId),
  };
}
